import type { ElementType } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';

import rbacCheck from '@/rbac/rbacCheck';
import type { RootState } from '@/redux/types';

interface ProtectedRouteProps {
  permission: string;
  component: ElementType;
  getData?: ((userRoles: string[], organization: unknown) => unknown) | null;
}

export const ProtectedRoute = ({
  permission,
  component: Component,
  getData = null,
}: ProtectedRouteProps) => {
  const userId = useSelector((state: RootState) => state.user._id);
  const userRoles = useSelector((state: RootState) => state.user.roles);
  const organization = useSelector((state: RootState) => state.user.organization);

  // `roles` is [] both before the user-data fetch resolves and for a genuinely
  // roleless user - `_id` only becomes non-empty once FETCH_USER_DATA_FULFILLED
  // actually lands, so it's what distinguishes "still loading" from "no access".
  // Without this, a direct/refreshed load races the fetch and redirects to 404
  // before roles ever arrive.
  if (!userId) {
    return null;
  }

  if (!userRoles || userRoles.length === 0) {
    return <Navigate to='/404' />;
  }

  const checkData = getData ? getData(userRoles, organization) : organization;

  const hasPermission = rbacCheck(userRoles, permission, checkData);

  if (!hasPermission) {
    return <Navigate to='/404' />;
  }

  return <Component />;
};
